import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocale } from "next-intl";
import classNames from "classnames";

import { NewIcon } from "ui/NewIcon";

import css from "./scroll-top.module.scss";

const ariaLabel = {
  pl: "Przewiń do góry",
  cz: "Posunout nahoru",
  sk: "Posuňte hore",
  de: "Nach oben scrollen",
  lt: "Slinkite į viršų",
  es: "Desplazarse hacia arriba",
};

const ScrollTop = () => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setVisible] = useState(false);
  const [isMounted, setMounted] = useState(false);
  const locale = useLocale();

  const scrollTop = useCallback(() => {
    window.scrollTo({ behavior: "smooth", top: 0 });
  }, []);

  useEffect(() => {
    const el = document.createElement("div");
    rootRef.current = el;
    document.body.appendChild(el);
    setMounted(true);

    return () => {
      if (el.parentNode) {
        el.parentNode.removeChild(el);
      }
      setMounted(false);
    };
  }, []);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setVisible(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  if (!isMounted || !rootRef.current) return null;

  return createPortal(
    <button
      className={classNames(css.button, { [css.visible]: isVisible })}
      type="button"
      onClick={scrollTop}
      aria-label={ariaLabel[locale] || ariaLabel.pl}
    >
      <NewIcon icon="chevron-up" width="28" height="28" />
    </button>,
    rootRef.current
  );
};

export default ScrollTop;
